import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import Table from '@mui/joy/Table';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import IconButton from '@mui/joy/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { productsApi } from '../../api/products.api';
import { categoriesApi } from '../../api/categories.api';
import { brandsApi } from '../../api/brands.api';
import { CreateProductDto } from '../../types/product';
import { Category } from '../../types/category';
import { Brand } from '../../types/brand';

type BulkProductRow = {
  name: string;
  price: string;
  quantity: string;
  sku?: string;
  categoryReference?: string;
  brandReference?: string;
};

const DEFAULT_NUM = '1';

const sanitizePriceInput = (value: string): string => {
  let v = value.replace(/[^\d.]/g, '');
  const dot = v.indexOf('.');
  if (dot === -1) return v;
  return `${v.slice(0, dot + 1)}${v.slice(dot + 1).replace(/\./g, '')}`;
};

const sanitizeQuantityInput = (value: string): string => value.replace(/\D/g, '');

const finalizePrice = (raw: string): string => {
  const t = raw.trim();
  if (t === '' || t === '.') return DEFAULT_NUM;
  const n = parseFloat(t);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_NUM;
  return String(n);
};

const finalizeQuantity = (raw: string): string => {
  const t = raw.trim();
  if (t === '') return DEFAULT_NUM;
  const n = parseInt(t, 10);
  if (!Number.isFinite(n) || n < 0) return DEFAULT_NUM;
  return String(n);
};

const emptyProduct = (): BulkProductRow => ({
  name: '',
  price: DEFAULT_NUM,
  quantity: DEFAULT_NUM,
  sku: undefined,
  categoryReference: undefined,
  brandReference: undefined,
});

const rowToDto = (row: BulkProductRow): CreateProductDto => ({
  name: row.name,
  price: parseFloat(finalizePrice(row.price)),
  quantity: parseInt(finalizeQuantity(row.quantity), 10),
  sku: row.sku,
  categoryReference: row.categoryReference,
  brandReference: row.brandReference,
});

const dtoToRow = (dto: CreateProductDto): BulkProductRow => ({
  name: dto.name,
  price: Number.isFinite(dto.price) ? finalizePrice(String(dto.price)) : DEFAULT_NUM,
  quantity: Number.isFinite(dto.quantity) ? finalizeQuantity(String(dto.quantity)) : DEFAULT_NUM,
  sku: dto.sku,
  categoryReference: dto.categoryReference,
  brandReference: dto.brandReference,
});

const FILTER_NONE = '';

const BulkProductCreator: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [products, setProducts] = useState<BulkProductRow[]>(() => [emptyProduct()]);
  const [results, setResults] = useState<{ success: number; errors: number }>({
    success: 0,
    errors: 0,
  });
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesData = await categoriesApi.getAll();
        const brandsData = await brandsApi.getAll();
        setCategories(categoriesData);
        setBrands(brandsData);
      } catch (error) {
        console.error('Error al cargar datos:', error);
      }
    };
    fetchData();
  }, []);

  const updateProduct = useCallback((index: number, patch: Partial<BulkProductRow>) => {
    setProducts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const handleAddRow = () => {
    setProducts((prev) => [...prev, emptyProduct()]);
  };

  const handleRemoveProduct = (index: number) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validProducts = products
      .filter((product) => product.name.trim() !== '')
      .map((row) => rowToDto(row));
    if (validProducts.length === 0) {
      alert('Por favor, ingresa al menos un producto válido.');
      return;
    }
    setLoading(true);
    setShowResults(false);
    try {
      const result = await productsApi.createBulk(validProducts);
      setResults({
        success: result.success.length,
        errors: result.errors.length,
      });
      setShowResults(true);
    } catch (error) {
      console.error('Error al crear productos:', error);
      setResults({ success: 0, errors: validProducts.length });
      setShowResults(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const content = event.target.result.toString();
        const lines = content.split('\n');
        const importedProducts: CreateProductDto[] = [];
        const dataLines = lines.slice(1);
        dataLines.forEach((line) => {
          if (line.trim()) {
            const [name, price, quantity, color, features, categoryReference, brandReference] = line
              .split(',')
              .map((item) => item.trim());
            if (name) {
              importedProducts.push({
                name,
                price: parseFloat(price) || 1,
                quantity: parseInt(quantity, 10) || 1,
                sku: undefined,
                categoryReference: categoryReference || undefined,
                brandReference: brandReference || undefined,
              });
            }
          }
        });
        if (importedProducts.length > 0) {
          setProducts(importedProducts.map(dtoToRow));
        } else {
          alert('No se encontraron productos válidos en el archivo.');
        }
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const csvContent =
      'Nombre,Precio,Cantidad,SKU,CategoríaRef,MarcaRef\nProducto Ejemplo 1,100,10,SKU-001,cat-ref-1,brand-ref-1\nProducto Ejemplo 2,200,20,SKU-002,cat-ref-2,brand-ref-2';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'plantilla_productos.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const bulkTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: 920,
    '& thead th': {
      bgcolor: 'background.surface',
      zIndex: 1,
      whiteSpace: 'nowrap',
      fontWeight: 'lg',
      fontSize: 'xs',
      py: 0.5,
      px: 1,
      verticalAlign: 'middle',
    },
    '& td': {
      verticalAlign: 'middle',
      py: 0.5,
      px: 1,
    },
  };

  const inputSlotPadding = { sx: { py: '10px', px: '12px', minHeight: 40, boxSizing: 'border-box' as const } };
  const selectButtonSlotSx = {
    py: '10px',
    px: '12px',
    minHeight: 40,
    boxSizing: 'border-box' as const,
    justifyContent: 'space-between',
  };

  return (
    <Box sx={{ p: 2, maxWidth: 'min(100%, 1600px)', mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 2 }}>
        Creación masiva de productos
      </Typography>

      {showResults ? (
        <Alert
          color={results.errors === 0 ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          endDecorator={
            <Button size="sm" onClick={() => navigate('/products')}>
              Ver todos los productos
            </Button>
          }
        >
          <Typography level="title-sm">Resultados</Typography>
          <Typography level="body-sm">{results.success} productos creados correctamente</Typography>
          {results.errors > 0 ? (
            <Typography level="body-sm">{results.errors} con errores</Typography>
          ) : null}
        </Alert>
      ) : null}

      <Sheet variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography level="title-md">Importar desde CSV</Typography>
              <Button size="sm" variant="plain" color="primary" onClick={downloadTemplate}>
                Descargar plantilla
              </Button>
            </Stack>
            <Button component="label" variant="outlined" color="neutral">
              Elegir archivo CSV
              <input type="file" accept=".csv" hidden onChange={handleImportFromCsv} />
            </Button>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
              Formato: Nombre, Precio, Cantidad, Color, Características, CategoríaRef, MarcaRef (encabezado en primera
              fila)
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Edición en tabla
            </Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 1.5 }}>
              Una fila por producto. Categoría y marca por lista; características separadas por comas.
            </Typography>
            <Box
              sx={{
                overflowX: 'auto',
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: 'min(70vh, 720px)',
                overflowY: 'auto',
              }}
            >
              <Table stickyHeader size="sm" borderAxis="bothBetween" sx={bulkTableSx}>
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '14%' }} />
                  <col />
                  <col style={{ width: 52 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Precio</th>
                    <th>Cant.</th>
                    <th>SKU</th>
                    <th>Categoría</th>
                    <th>Marca</th>
                    <th aria-label="Eliminar" />
                  </tr>
                </thead>
                <tbody>
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <Typography level="body-sm" sx={{ color: 'neutral.500', py: 2, textAlign: 'center' }}>
                          No hay filas. Usá «Agregar otra fila» para comenzar.
                        </Typography>
                      </td>
                    </tr>
                  ) : null}
                  {products.map((product, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: 'center' }}>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {index + 1}
                        </Typography>
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          placeholder="Nombre"
                          value={product.name}
                          onChange={(e) => updateProduct(index, { name: e.target.value })}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          type="text"
                          inputMode="decimal"
                          placeholder={DEFAULT_NUM}
                          value={product.price}
                          onChange={(e) => updateProduct(index, { price: sanitizePriceInput(e.target.value) })}
                          onBlur={() => {
                            const next = finalizePrice(product.price);
                            if (next !== product.price) updateProduct(index, { price: next });
                          }}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          type="text"
                          inputMode="numeric"
                          placeholder={DEFAULT_NUM}
                          value={product.quantity}
                          onChange={(e) => updateProduct(index, { quantity: sanitizeQuantityInput(e.target.value) })}
                          onBlur={() => {
                            const next = finalizeQuantity(product.quantity);
                            if (next !== product.quantity) updateProduct(index, { quantity: next });
                          }}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          placeholder="SKU"
                          value={product.sku || ''}
                          onChange={(e) => updateProduct(index, { sku: e.target.value })}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Select
                          size="sm"
                          variant="soft"
                          color="neutral"
                          value={product.categoryReference ?? FILTER_NONE}
                          onChange={(_, value) => {
                            const v = value as string;
                            updateProduct(index, { categoryReference: v === FILTER_NONE ? undefined : v });
                          }}
                          slotProps={{ button: { type: 'button', sx: selectButtonSlotSx } }}
                          sx={{ width: '100%' }}
                        >
                          <Option value={FILTER_NONE}>—</Option>
                          {categories.map((c) => (
                            <Option key={c.reference} value={c.reference}>
                              {c.name}
                            </Option>
                          ))}
                        </Select>
                      </td>
                      <td>
                        <Select
                          size="sm"
                          variant="soft"
                          color="neutral"
                          value={product.brandReference ?? FILTER_NONE}
                          onChange={(_, value) => {
                            const v = value as string;
                            updateProduct(index, { brandReference: v === FILTER_NONE ? undefined : v });
                          }}
                          slotProps={{ button: { type: 'button', sx: selectButtonSlotSx } }}
                          sx={{ width: '100%' }}
                        >
                          <Option value={FILTER_NONE}>—</Option>
                          {brands.map((b) => (
                            <Option key={b.reference} value={b.reference}>
                              {b.name}
                            </Option>
                          ))}
                        </Select>
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <IconButton
                          type="button"
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveProduct(index)}
                          aria-label="Eliminar fila"
                          sx={{ flexShrink: 0 }}
                        >
                          <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>

            <Button type="button" variant="outlined" color="neutral" onClick={handleAddRow} sx={{ mt: 1.5 }}>
              + Agregar otra fila
            </Button>

            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 2 }}>
              <Button type="submit" color="primary" loading={loading}>
                Crear productos
              </Button>
              <Button type="button" variant="outlined" color="neutral" onClick={() => navigate('/products')}>
                Cancelar
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Sheet>
    </Box>
  );
};

export default BulkProductCreator;
